/* eslint-disable no-console */
import jwt_decode, { InvalidTokenError, JwtPayload } from 'jwt-decode';

import { SCOPE, TOKEN_STORAGE_KEY } from '@/config/auth.config';
import { IframeMessage, IFRAME_MESSAGE_TYPES } from '@/config/iframe.config';
import { EnvUtils } from '../env.utils';
import { LocationUtils } from './location.utils';


export function isTokenExpired(tokenString: string): boolean {
  const tokenPayload: JwtPayload = jwt_decode<JwtPayload>(tokenString);

  if (typeof tokenPayload?.exp === 'number') {
    return tokenPayload.exp * 1000 < Date.now();
  }

  throw new InvalidTokenError();
}


export async function getAccessTokenDevEnv(): Promise<string> {
  const token: string | null = localStorage.getItem(TOKEN_STORAGE_KEY);
  const ENDPOINT: string | undefined = process.env.ACCESS_TOKEN_DEV_ENDPOINT;
  const USERNAME: string | undefined = process.env.ACCESS_TOKEN_DEV_USERNAME;
  const PASSWORD: string | undefined = process.env.ACCESS_TOKEN_DEV_PASSWORD;
  const EMAIL: string | undefined = process.env.ACCESS_TOKEN_DEV_EMAIL;
  const USER_ID: string | undefined = process.env.ACCESS_TOKEN_DEV_USER_ID;

  if (!ENDPOINT || !USERNAME || !PASSWORD || !EMAIL || !USER_ID) {
    throw new Error('Not specified params to get access token');
  }

  if (typeof token === 'string' && !isTokenExpired(token)) {
    return token;
  }

  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      Authorization: `Basic ${btoa(`${USERNAME}:${PASSWORD}`)}`,
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: new URLSearchParams({
      email: EMAIL,
      grant_type: 'test_impersonation',
      scope: SCOPE,
      acid: USER_ID
    })
  });

  const result = await response.json();

  if (typeof result?.access_token === 'string') {
    window?.localStorage?.setItem(TOKEN_STORAGE_KEY, result.access_token);
    return result.access_token;
  }

  throw new Error('An error occurred while requesting the token');
}

export async function getAccessToken(): Promise<string> {
  if (EnvUtils.isDevelopment()) {
    return getAccessTokenDevEnv();
  }

  return new Promise((resolve) => {
    (async () => {
      try {
        const workspaceUrl = await LocationUtils.getWorkspaceUrl();
        const targetOrigin = new URL(workspaceUrl).origin;
        function getAccessTokenMessageCallback(event: MessageEvent) {
          if (event.origin !== targetOrigin) return;
          if (event?.data?.agToken) {
            window.removeEventListener('message', getAccessTokenMessageCallback);
            resolve(event?.data?.agToken);
          }
        }

        const message: IframeMessage = {
          type: IFRAME_MESSAGE_TYPES.GET_ACCESS_TOKEN_FROM_WORKSPACE,
        };

        window.addEventListener('message', getAccessTokenMessageCallback);
        window?.parent?.postMessage(JSON.stringify(message), targetOrigin);
      } catch (error) {
        console.error(error);
      }
    })();
  });
}
